import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const formatDetail = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Could not reset password. Please request a new link.";
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      toast.success("Password updated. Please sign in.");
      navigate("/admin-login");
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="h-24 flex items-center px-5 sm:px-8">
        <BrandLogo testId="reset-logo" />
      </header>
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8" data-testid="reset-password-card">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5"><Lock className="w-6 h-6 text-primary" /></div>
          <h1 className="font-heading font-extrabold text-2xl text-slate-900">Choose a new password</h1>
          {!token ? (
            <p className="font-body text-sm text-rose-600 mt-2" data-testid="reset-missing-token">This link is missing its token. <Link to="/forgot-password" className="font-semibold underline">Request a new one</Link>.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pwd" className="font-heading font-bold text-slate-800">New password</Label>
                <Input id="pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="At least 8 characters" className="mt-1.5 rounded-xl" data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="cf" className="font-heading font-bold text-slate-800">Confirm password</Label>
                <Input id="cf" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 rounded-xl" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-xl h-12 font-heading font-bold" data-testid="reset-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Update password
              </Button>
            </form>
          )}
          <p className="font-body text-xs text-slate-400 mt-6 text-center">
            <Link to="/admin-login" className="text-primary font-semibold" data-testid="reset-back-link">Back to sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
